/**
 * @fileoverview Admin API Contract (Contract-First Design)
 * @module types/contracts
 *
 * Complete API contract for the Admin module.
 * Handles admin dashboard, system management, and data cleanup.
 */

import { z } from 'zod';
import {
	createContract,
	ApiSuccessResponseSchema,
	ApiPaginatedResponseSchema,
} from './contract.utils';
import {
	UserIdParamSchema,
	CreateUserSchema,
	UpdateUserSchema,
	ListUsersQuerySchema,
	UserApiResponseSchema,
	PaginatedUsersApiResponseSchema,
	DeleteUserApiResponseSchema,
} from './users.contract';
import { PaymentStatus } from './payment.contract';
import { HealthStatus } from '../api.types';

// ============================================================================
// SCHEMAS (Data Structure & Validation)
// ============================================================================

/**
 * System Overview Schema
 * Matches the actual service implementation
 */
export const SystemOverviewSchema = z.object({
	totalUsers: z.number().int().nonnegative(),
	totalSessions: z.number().int().nonnegative(),
	totalTestResults: z.number().int().nonnegative(),
	totalPayments: z.number().int().nonnegative(),
	totalAnalyses: z.number().int().nonnegative(),
	totalReviews: z.number().int().nonnegative(),
	totalEmails: z.number().int().nonnegative(),
	totalPdfs: z.number().int().nonnegative(),
	recentActivity: z.object({
		newUsers: z.number().int().nonnegative(),
		newTestResults: z.number().int().nonnegative(),
		newPayments: z.number().int().nonnegative(),
		newAnalyses: z.number().int().nonnegative(),
	}),
});
export type SystemOverview = z.infer<typeof SystemOverviewSchema>;

/**
 * System Health Schema
 */
export const SystemHealthSchema = z.object({
	status: z.enum(HealthStatus),
	database: z.object({
		connected: z.boolean(),
		responseTime: z.number().nonnegative(),
	}),
	redis: z.object({
		connected: z.boolean(),
		responseTime: z.number().nonnegative(),
	}),
	openai: z.object({
		available: z.boolean(),
		model: z.string().optional(),
	}),
	email: z.object({
		configured: z.boolean(),
	}),
	uptime: z.number().nonnegative(),
	timestamp: z.iso.datetime(),
});
export type SystemHealth = z.infer<typeof SystemHealthSchema>;

/**
 * Data Cleanup Options Schema
 * Matches the actual service implementation
 */
export const DataCleanupOptionsSchema = z.object({
	cleanupSessions: z.boolean().default(false),
	sessionMaxAge: z.number().int().positive().default(30),
	cleanupFailedPayments: z.boolean().default(false),
	paymentMaxAge: z.number().int().positive().default(90),
	cleanupRejectedAnalyses: z.boolean().default(false),
	analysisMaxAge: z.number().int().positive().default(60),
	cleanupOldReviews: z.boolean().default(false),
	reviewMaxAge: z.number().int().positive().default(90),
	dryRun: z.boolean().default(true),
});
export type DataCleanupOptions = z.infer<typeof DataCleanupOptionsSchema>;

/**
 * Data Cleanup Result Schema
 * Matches the actual service implementation
 */
export const DataCleanupResultSchema = z.object({
	success: z.boolean(),
	dryRun: z.boolean().optional(),
	deletedSessions: z.number().int().nonnegative().optional(),
	deletedPayments: z.number().int().nonnegative().optional(),
	deletedAnalyses: z.number().int().nonnegative().optional(),
	deletedReviews: z.number().int().nonnegative().optional(),
	message: z.string().optional(),
});
export type DataCleanupResult = z.infer<typeof DataCleanupResultSchema>;

/**
 * Clear Cache Result Schema
 */
export const ClearCacheResultSchema = z.object({
	success: z.boolean(),
	clearedKeys: z.number().int().nonnegative(),
	message: z.string(),
});
export type ClearCacheResult = z.infer<typeof ClearCacheResultSchema>;

/**
 * Backup Result Schema
 */
export const BackupResultSchema = z.object({
	success: z.boolean(),
	backupPath: z.string().optional(),
	backupSize: z.number().int().nonnegative().optional(),
	timestamp: z.iso.datetime(),
	message: z.string(),
});
export type BackupResult = z.infer<typeof BackupResultSchema>;

/**
 * Admin User Management Query Schema
 * Extends ListUsersQuerySchema with search capability for admin interface
 */
export const AdminUserManagementQuerySchema = ListUsersQuerySchema.extend({
	search: z.string().optional().describe('Search by name or email'),
});
export type AdminUserManagementQuery = z.infer<typeof AdminUserManagementQuerySchema>;

/**
 * Payment Management Query Schema
 * Matches the actual service implementation
 */
export const PaymentManagementQuerySchema = z.object({
	status: z.enum(PaymentStatus).optional(),
	userId: z.uuid().optional(),
	page: z.coerce.number().int().positive().default(1),
	limit: z.coerce.number().int().positive().max(100).default(20),
});
export type PaymentManagementQuery = z.infer<typeof PaymentManagementQuerySchema>;

/**
 * Session Management Query Schema
 * Matches the actual service implementation
 */
export const SessionManagementQuerySchema = z.object({
	status: z.enum(['active', 'expired', 'completed', 'converted', 'abandoned']).optional(),
	userId: z.uuid().optional(),
	page: z.coerce.number().int().positive().default(1),
	limit: z.coerce.number().int().positive().max(100).default(20),
});
export type SessionManagementQuery = z.infer<typeof SessionManagementQuerySchema>;

/**
 * Reset User Password Request Schema
 */
export const ResetUserPasswordRequestSchema = z.object({
	userId: z.uuid(),
});
export type ResetUserPasswordRequest = z.infer<typeof ResetUserPasswordRequestSchema>;

/**
 * Reset User Password Response Schema
 */
export const ResetUserPasswordResponseSchema = z.object({
	success: z.boolean(),
	message: z.string(),
	resetToken: z.string().optional(),
});
export type ResetUserPasswordResponse = z.infer<typeof ResetUserPasswordResponseSchema>;

// ============================================================================
// API RESPONSE WRAPPERS
// ============================================================================

export const SystemOverviewApiResponseSchema = ApiSuccessResponseSchema(SystemOverviewSchema);
export const SystemHealthApiResponseSchema = ApiSuccessResponseSchema(SystemHealthSchema);
export const DataCleanupResultApiResponseSchema = ApiSuccessResponseSchema(DataCleanupResultSchema);
export const ClearCacheResultApiResponseSchema = ApiSuccessResponseSchema(ClearCacheResultSchema);
export const BackupResultApiResponseSchema = ApiSuccessResponseSchema(BackupResultSchema);
export const ResetUserPasswordApiResponseSchema = ApiSuccessResponseSchema(ResetUserPasswordResponseSchema);
export const PaginatedUsersAdminApiResponseSchema = ApiPaginatedResponseSchema(z.any()); // User schema from users module
export const PaginatedPaymentsAdminApiResponseSchema = ApiPaginatedResponseSchema(z.any()); // Payment schema from payment module
export const PaginatedSessionsAdminApiResponseSchema = ApiPaginatedResponseSchema(z.any()); // Session schema from session module
export const PaginatedTestResultsAdminApiResponseSchema = ApiPaginatedResponseSchema(z.any()); // TestResult schema from test-results module

// ============================================================================
// API CONTRACT (Routes, Methods, Request/Response)
// ============================================================================

/**
 * Admin API Contract
 *
 * Defines the complete API surface for the Admin module.
 * Both frontend and backend use this as the single source of truth.
 */
export const ADMIN_API_CONTRACT = createContract('/admin', {
	/**
	 * GET /admin/overview
	 * Get system overview (dashboard)
	 */
	getOverview: {
		method: 'GET' as const,
		path: '/overview',
		response: SystemOverviewApiResponseSchema,
	},

	/**
	 * GET /admin/health
	 * Get system health status
	 */
	getHealth: {
		method: 'GET' as const,
		path: '/health',
		response: SystemHealthApiResponseSchema,
	},

	/**
	 * POST /admin/cleanup
	 * Clean up old data
	 */
	cleanup: {
		method: 'POST' as const,
		path: '/cleanup',
		body: DataCleanupOptionsSchema,
		response: DataCleanupResultApiResponseSchema,
	},

	/**
	 * POST /admin/cache/clear
	 * Clear Redis cache
	 */
	clearCache: {
		method: 'POST' as const,
		path: '/cache/clear',
		response: ClearCacheResultApiResponseSchema,
	},

	/**
	 * POST /admin/backup
	 * Create database backup
	 */
	createBackup: {
		method: 'POST' as const,
		path: '/backup',
		response: BackupResultApiResponseSchema,
	},

	// ========================================================================
	// USER MANAGEMENT (Admin-only CRUD operations)
	// ========================================================================

	/**
	 * GET /admin/users
	 * List all users (paginated, searchable)
	 */
	getUsers: {
		method: 'GET' as const,
		path: '/users',
		query: AdminUserManagementQuerySchema,
		response: PaginatedUsersApiResponseSchema,
	},

	/**
	 * GET /admin/users/:id
	 * Get single user by ID
	 */
	getUser: {
		method: 'GET' as const,
		path: '/users/:id',
		params: UserIdParamSchema,
		response: UserApiResponseSchema,
	},

	/**
	 * POST /admin/users
	 * Create a new user
	 */
	createUser: {
		method: 'POST' as const,
		path: '/users',
		body: CreateUserSchema,
		response: UserApiResponseSchema,
	},

	/**
	 * PUT /admin/users/:id
	 * Update a user
	 */
	updateUser: {
		method: 'PUT' as const,
		path: '/users/:id',
		params: UserIdParamSchema,
		body: UpdateUserSchema,
		response: UserApiResponseSchema,
	},

	/**
	 * DELETE /admin/users/:id
	 * Delete a user
	 */
	deleteUser: {
		method: 'DELETE' as const,
		path: '/users/:id',
		params: UserIdParamSchema,
		response: DeleteUserApiResponseSchema,
	},

	/**
	 * GET /admin/payments
	 * Get payments (paginated, filterable)
	 */
	getPayments: {
		method: 'GET' as const,
		path: '/payments',
		query: PaymentManagementQuerySchema,
		response: z.object({
			payments: z.array(z.any()), // Use Payment schema from payment.contract
			pagination: z.object({
				page: z.number().int().positive(),
				limit: z.number().int().positive(),
				total: z.number().int().nonnegative(),
				totalPages: z.number().int().nonnegative(),
			}),
		}),
	},

	/**
	 * GET /admin/sessions
	 * Get sessions (paginated, filterable)
	 */
	getSessions: {
		method: 'GET' as const,
		path: '/sessions',
		query: SessionManagementQuerySchema,
		response: z.object({
			sessions: z.array(z.any()), // Use SessionTracking schema from session.contract
			pagination: z.object({
				page: z.number().int().positive(),
				limit: z.number().int().positive(),
				total: z.number().int().nonnegative(),
				totalPages: z.number().int().nonnegative(),
			}),
		}),
	},

	/**
	 * POST /admin/users/:userId/reset-password
	 * Reset user password (admin only)
	 */
	resetUserPassword: {
		method: 'POST' as const,
		path: '/users/:userId/reset-password',
		params: z.object({ userId: z.uuid() }),
		response: ResetUserPasswordApiResponseSchema,
	},
});

/**
 * Route Constants for Controllers
 * Use these in @Controller() and @Get/@Post decorators
 */
export const ADMIN_ROUTES = {
	base: ADMIN_API_CONTRACT.base,
	getOverview: ADMIN_API_CONTRACT.routes.getOverview.path,
	getHealth: ADMIN_API_CONTRACT.routes.getHealth.path,
	cleanup: ADMIN_API_CONTRACT.routes.cleanup.path,
	getUsers: ADMIN_API_CONTRACT.routes.getUsers.path,
	getPayments: ADMIN_API_CONTRACT.routes.getPayments.path,
	getSessions: ADMIN_API_CONTRACT.routes.getSessions.path,
};

// ============================================================================
// TYPE EXPORTS
// ============================================================================

/**
 * Export all types for use in frontend and backend
 */
export type AdminApiContract = typeof ADMIN_API_CONTRACT;
